import React from 'react';
import type { TradingAnalysis } from '../../types/trading';

interface ConfluenceDetailProps {
  analysis: TradingAnalysis;
  compact?: boolean;
}

const badgeColor: Record<string, string> = {
  bullish: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  bearish: 'bg-red-500/15 text-red-400 border-red-500/30',
  strong_bullish: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  strong_bearish: 'bg-red-500/20 text-red-300 border-red-500/40',
  neutral: 'bg-slate-700/50 text-slate-400 border-slate-600/30',
  ranging: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  trending: 'bg-teal-500/15 text-teal-400 border-teal-500/30',
  high: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  low: 'bg-slate-700/50 text-slate-400 border-slate-600/30',
  normal: 'bg-slate-700/50 text-slate-300 border-slate-600/30',
  filled: 'bg-slate-700/50 text-slate-400 border-slate-600/30',
  unfilled: 'bg-teal-500/15 text-teal-400 border-teal-500/30',
  partial: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
};

const Badge: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const color = badgeColor[value?.toLowerCase()] || 'bg-slate-700/50 text-slate-400 border-slate-600/30';
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-500 min-w-[80px]">{label}</span>
      <span className={`text-xs px-2 py-0.5 rounded border ${color}`}>
        {value || '—'}
      </span>
    </div>
  );
};

const PriceTag: React.FC<{ label: string; value: number | null | undefined; color?: string }> = ({ label, value, color = 'text-slate-300' }) => {
  if (value === null || value === undefined) return null;
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-xs font-mono ${color}`}>{value.toFixed(2)}</span>
    </div>
  );
};

const ZoneDisplay: React.FC<{ label: string; zone: [number, number] | null | undefined; color: string }> = ({ label, zone, color }) => {
  if (!zone || !Array.isArray(zone) || zone.length < 2) return null;
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-xs font-mono ${color}`}>{zone[0].toFixed(2)} — {zone[1].toFixed(2)}</span>
    </div>
  );
};

const ConfluenceDetail: React.FC<ConfluenceDetailProps> = ({ analysis, compact = false }) => {
  const { key_levels, fair_value_gaps, indicators } = analysis;

  const hasKeyLevels = key_levels && (
    key_levels.nearest_support != null ||
    key_levels.nearest_resistance != null ||
    key_levels.supply_zone != null ||
    key_levels.demand_zone != null
  );

  const hasFVGs = fair_value_gaps && (
    fair_value_gaps.bullish_fvg != null ||
    fair_value_gaps.bearish_fvg != null
  );

  const hasIndicators = indicators && (
    indicators.obv_signal ||
    indicators.atr_regime ||
    indicators.trend_direction
  );

  if (!hasKeyLevels && !hasFVGs && !hasIndicators) return null;

  // Access additional fields that may exist in the raw data
  const kl = key_levels as Record<string, unknown> | null;
  const fvg = fair_value_gaps as Record<string, unknown> | null;

  return (
    <div className={`space-y-3 ${compact ? 'text-xs' : ''}`}>
      {/* Key Levels */}
      {hasKeyLevels && (
        <div className="bg-slate-900/50 rounded-lg border border-slate-700/30 p-3">
          <h4 className="text-xs font-medium text-slate-400 mb-2">Key Levels</h4>
          <div className="space-y-1.5">
            <PriceTag label="Support" value={key_levels!.nearest_support} color="text-emerald-400" />
            <PriceTag label="Resistance" value={key_levels!.nearest_resistance} color="text-red-400" />
            <ZoneDisplay label="Demand Zone" zone={key_levels!.demand_zone} color="text-emerald-300" />
            <ZoneDisplay label="Supply Zone" zone={key_levels!.supply_zone} color="text-red-300" />
            {kl?.liquidity_above != null && (
              <PriceTag label="Liquidity Above" value={kl.liquidity_above as number} color="text-amber-400" />
            )}
            {kl?.liquidity_below != null && (
              <PriceTag label="Liquidity Below" value={kl.liquidity_below as number} color="text-amber-400" />
            )}
            <div className="flex gap-2 mt-1.5 flex-wrap">
              {!!kl?.recent_sweep && (
                <span className="text-xs px-2 py-0.5 rounded border bg-amber-500/15 text-amber-400 border-amber-500/30">
                  Liquidity Sweep
                </span>
              )}
              {!!kl?.trapped_traders && (
                <span className="text-xs px-2 py-0.5 rounded border bg-purple-500/15 text-purple-400 border-purple-500/30">
                  Trapped Traders
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fair Value Gaps */}
      {hasFVGs && (
        <div className="bg-slate-900/50 rounded-lg border border-slate-700/30 p-3">
          <h4 className="text-xs font-medium text-slate-400 mb-2">Fair Value Gaps</h4>
          <div className="space-y-1.5">
            <ZoneDisplay label="Bullish FVG" zone={fair_value_gaps!.bullish_fvg} color="text-emerald-300" />
            <ZoneDisplay label="Bearish FVG" zone={fair_value_gaps!.bearish_fvg} color="text-red-300" />
            {fair_value_gaps!.fill_status && (
              <Badge label="Fill Status" value={fair_value_gaps!.fill_status} />
            )}
            {fair_value_gaps!.fvg_confluence && (
              <Badge label="Confluence" value={fair_value_gaps!.fvg_confluence} />
            )}
            {typeof fvg?.confluence_type === 'string' && (
              <Badge label="Type" value={fvg.confluence_type} />
            )}
          </div>
        </div>
      )}

      {/* Indicators */}
      {hasIndicators && (
        <div className="bg-slate-900/50 rounded-lg border border-slate-700/30 p-3">
          <h4 className="text-xs font-medium text-slate-400 mb-2">Indicators</h4>
          <div className="space-y-1.5">
            {indicators!.obv_signal && <Badge label="OBV" value={indicators!.obv_signal} />}
            {indicators!.atr_regime && <Badge label="ATR Regime" value={indicators!.atr_regime} />}
            {indicators!.trend_direction && <Badge label="Trend" value={indicators!.trend_direction} />}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfluenceDetail;
